import * as _ from 'lodash';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { VoidFunctionComponent } from 'react';
import type { XAxisProps } from 'recharts';

import { abbrevNumber, prettyNumber } from '../../../../shared/utils';
import COLORS from '../../../theme/colors';
import { fossaPrimaryFont } from '../../../theme/typography';
import type { FOSSAColor } from '../../../theme/colors';

type LineProps = {
  dataKey: string;
  color?: FOSSAColor;
};

type AreaProps = {
  dataKey: string;
  strokeColor?: FOSSAColor;
  fillColor?: FOSSAColor;
};

export type Point = {
  name: string | number | null;
  // We nest the data so that we can enforce the `name` field through typings.
  data: Record<string, number>;
};

type Props = {
  /**
   * The data that will be used to populate the graph. This array **needs** to be in the order you want them along the X axis.
   * All keys in the nested `data` field should match the `dataKeys` in the `lines` configurations.
   */
  points: Point[];
  /**
   * The configurations for the lines in the graph. The `dataKey` values should match the keys in the `points.data` objects.
   * This tells recharts what key to look for in the `points.data` object and what color to make the line.
   */
  lines?: LineProps[];
  /**
   * Configuration for areas on the graph.  The `dataKey` values should match keys in the `points.data` objects.
   */
  areas?: AreaProps[];
  xAxisConfig?: {
    tickFormatter: XAxisProps['tickFormatter'];
  }
  yAxisConfig?: {
    label?: string;
  }
  /**
   * Whether or not to show the legend. The legend will appear at in the center beneath the line graph.
   */
  showLegend?: boolean;
  /**
   * Whether or not to show tooltips when hovering over the graph.
   */
  showTooltips?: boolean;
};

/**
 * A generic for generating a FOSSA stylized line graph.
 */
const Graph: VoidFunctionComponent<Props> = ({
  points,
  lines,
  areas,
  xAxisConfig,
  yAxisConfig,
  showLegend,
  showTooltips,
}) => {
  return (
    <ResponsiveContainer height={300}>
      <ComposedChart data={points.map(({ name, data }) => ({ name, ...data }))}>
        <CartesianGrid strokeDasharray="3" stroke={COLORS.fossaGray20} />
        <XAxis
          dataKey="name"
          tickLine={false}
          tickFormatter={xAxisConfig?.tickFormatter}
          padding={{ left: 0, right: 0 }}
          axisLine={{
            stroke: COLORS.fossaGray20,
          }}
          tick={{
            fontSize: 12,
            fontFamily: 'iA Writer Quattro',
          }}
          minTickGap={80}
        />
        <YAxis
          tickLine={false}
          tick={{
            fontSize: 12,
            fontFamily: 'iA Writer Quattro',
          }}
          axisLine={{
            stroke: COLORS.fossaGray20,
          }}
          tickFormatter={(value: number) => _.isNumber(value) ? abbrevNumber(value) : value}
        >
          <Label
            angle={-90}
            fontSize={12}
            style={{ textAnchor: 'middle' }}
            position="insideLeft"
            fontFamily="iA Writer Quattro"
          >
            {yAxisConfig?.label}
          </Label>
        </YAxis>
        {(lines ?? []).map((line) => (
          <Line
            key={line.dataKey}
            type="monotone"
            dataKey={line.dataKey}
            stroke={line.color ?? COLORS.fossaBlue}
            dot={false}
            strokeWidth={2}
            connectNulls
          />
        ))}
        {(areas ?? []).map((area) => (
          <Area
            key={area.dataKey}
            type="monotone"
            dataKey={area.dataKey}
            stroke={area.strokeColor ?? COLORS.fossaBlue}
            fill={area.fillColor ?? area.strokeColor ?? COLORS.fossaBlue}
            strokeWidth={1}
            stackId="1"
          />
        ))}
        {showLegend && (
          <Legend verticalAlign="bottom" iconType="line" align="center" fontFamily="iA Writer Quattro" formatter={_.capitalize} />
        )}
        {showTooltips && (
          <Tooltip
            formatter={(value, name) => [prettyNumber(value as number), _.capitalize(name as string)]}
            labelStyle={{ fontFamily: fossaPrimaryFont, fontWeight: 'bold' }}
            contentStyle={{ fontFamily: fossaPrimaryFont }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default Graph;
